import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Sparkles, Check, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const PT_VAT_RATES = [0, 4, 12, 22];  // taxas Região Autónoma da Madeira

/**
 * "Importar de fatura" modal.
 *
 * Flow:
 *  1. User picks an invoice photo (JPEG/PNG/WEBP, ≤ 10 MB).
 *  2. POST /api/admin/ledger/parse-invoice → AI returns parsed items[].
 *  3. Items appear in an editable preview table — admin tweaks values,
 *     toggles rows on/off, then commits.
 *  4. POST /api/admin/ledger/bulk → all selected rows inserted in one shot
 *     for the month currently being viewed.
 */
export default function InvoiceImportModal({ open, onClose, month, onImported }) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);  // dataURL for thumbnail
  const [parsing, setParsing] = useState(false);
  const [items, setItems] = useState([]);  // parsed + editable rows
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  function reset() {
    setFile(null); setPreview(null); setItems([]);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose() { reset(); onClose(); }

  async function pickFile(f) {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Formato não suportado. Use JPEG, PNG ou WEBP.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx. 10 MB).");
      return;
    }
    setFile(f);
    setItems([]);
    const url = URL.createObjectURL(f);
    setPreview(url);
  }

  async function runOcr() {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ledger/parse-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const aiItems = data.items || [];

      // Server-side dedupe check across BOTH categories (produto + material) —
      // an item with the same name in either category counts as a duplicate.
      let dupMap = {};
      if (aiItems.length) {
        try {
          const names = aiItems.map(i => i.name || "");
          const [{ data: dupP }, { data: dupM }] = await Promise.all([
            api.post("/admin/ledger/check-duplicates", { month, category: "produto", names }),
            api.post("/admin/ledger/check-duplicates", { month, category: "material", names }),
          ]);
          for (const k of Object.keys(dupP.by_input || {})) {
            dupMap[k] = !!dupP.by_input[k] || !!(dupM.by_input || {})[k];
          }
        } catch { /* fail-open: treat as all new */ }
      }

      const parsed = aiItems.map((it, i) => {
        const exists = !!dupMap[it.name];
        return {
          id: `tmp-${i}`,
          selected: !exists,        // dedupe: pre-uncheck if already in this month
          existing: exists,
          category: "produto",      // default: produto. Admin can switch to "material" per row.
          code: it.code || "",
          name: it.name || "",
          unit_price: it.unit_price ?? 0,
          quantity: it.quantity ?? 0,
          vat_rate: it.vat_rate ?? 22,
        };
      });
      setItems(parsed);
      const newCount = parsed.filter(p => !p.existing).length;
      const dupCount = parsed.length - newCount;
      if (!parsed.length) {
        toast.warning("A IA não detetou nenhuma linha. Tente outra foto mais nítida.");
      } else if (dupCount > 0) {
        toast.success(`${parsed.length} linhas detetadas · ${newCount} novas · ${dupCount} já existem`);
      } else {
        toast.success(`${parsed.length} novas linhas detetadas.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setParsing(false); }
  }

  function patch(id, partial) {
    setItems(rows => rows.map(r => r.id === id ? { ...r, ...partial } : r));
  }

  async function commit() {
    const chosen = items.filter(i => i.selected && i.name && i.unit_price > 0);
    if (!chosen.length) {
      toast.error("Selecione pelo menos uma linha válida.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/ledger/bulk", {
        month,
        rows: chosen.map(r => ({
          category: r.category === "material" ? "material" : "produto",
          name: r.name.trim(),
          code: (r.code || "").trim(),
          unit_price: Number(r.unit_price) || 0,
          quantity: Number(r.quantity) || 0,
          vat_rate: Number(r.vat_rate) || 0,
        })),
      });
      const nProd = chosen.filter(r => r.category !== "material").length;
      const nMat = chosen.filter(r => r.category === "material").length;
      const parts = [];
      if (nProd) parts.push(`${nProd} produto(s)`);
      if (nMat) parts.push(`${nMat} material(is)`);
      const ftrNum = data?.ftr?.number;
      if (ftrNum) {
        // Reinforce the behaviour the admin asked about: the OCR import created
        // a FTR automatically, and he can jump straight to it.
        toast.success(
          `${parts.join(" · ")} importado(s) · fatura interna ${ftrNum} criada`,
          {
            action: {
              label: "Ver fatura",
              onClick: () => nav("/admin/faturas-stock"),
            },
            duration: 8000,
          },
        );
      } else {
        toast.success(`${parts.join(" · ")} importado(s).`);
      }
      onImported && onImported();
      handleClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  const totalCount = items.filter(i => i.selected).length;
  const totalValue = items
    .filter(i => i.selected)
    .reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.quantity) || 0)
                          * (1 + (Number(i.vat_rate) || 0) / 100), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={handleClose}
         data-testid="invoice-import-modal">
      <div className="lux-card w-full max-w-5xl max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-5 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Sparkles size={12} /> Importar de fatura · IA
            </div>
            <div className="font-display text-xl">Captura de produtos por foto</div>
          </div>
          <button onClick={handleClose}
                  className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="invoice-modal-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Step 1 — upload */}
          {!items.length && (
            <div className="space-y-3">
              <div className="text-sm text-[#a0b2af]">
                Tire uma foto nítida da fatura/recibo do fornecedor. A IA vai
                extrair: <span className="text-[#d4af37]">Código</span>,
                {" "}<span className="text-[#d4af37]">Descrição</span>,
                {" "}<span className="text-[#d4af37]">Qt. Total</span> e
                {" "}<span className="text-[#d4af37]">Preço Uni.</span>
                {" "}As linhas aparecem pela <span className="text-[#d4af37]">mesma ordem da fatura</span>.
                {" "}Para cada linha pode escolher se é <span className="text-[#d4af37]">Produto</span> (consumível)
                ou <span className="text-[#d4af37]">Material</span> (durável de limpeza).
              </div>
              <div className="text-xs text-[#d4af37]/80 bg-[#d4af37]/8 border border-[#d4af37]/30 rounded-sm px-3 py-2 flex items-start gap-2"
                   data-testid="invoice-autoftr-hint">
                <Sparkles size={12} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Importação automática:</strong> ao gravar, o sistema cria
                  automaticamente uma <span className="font-mono">FTR-NNNNNN</span> em
                  «Stock de produtos» com todos os itens desta importação, e
                  atualiza o stock de cada produto. Evita inserção dupla.
                </div>
              </div>

              <label htmlFor="invoice-upload"
                     className="block lux-card border-dashed border-[#d4af37]/40 p-10 text-center cursor-pointer hover:bg-[#d4af37]/5 transition-colors"
                     data-testid="invoice-upload-dropzone">
                {preview ? (
                  <div className="space-y-3">
                    <img src={preview} alt="preview"
                         className="max-h-64 mx-auto rounded-sm border border-[#1a4640]" />
                    <div className="text-xs text-[#a0b2af]">
                      {file.name} · {(file.size / 1024).toFixed(0)} KB · clique para escolher outro
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 text-[#a0b2af]">
                    <Upload size={36} className="mx-auto text-[#d4af37]" />
                    <div className="text-sm">Clique aqui ou arraste a foto da fatura</div>
                    <div className="text-[10px] uppercase tracking-widest">
                      JPEG · PNG · WEBP — até 10 MB
                    </div>
                  </div>
                )}
              </label>
              <input id="invoice-upload" ref={inputRef} type="file"
                     accept="image/jpeg,image/png,image/webp"
                     className="hidden"
                     onChange={e => pickFile(e.target.files?.[0])}
                     data-testid="invoice-upload-input" />

              {file && (
                <button onClick={runOcr}
                        disabled={parsing}
                        className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="invoice-run-ocr">
                  {parsing
                    ? <><Loader2 size={14} className="animate-spin" /> A analisar fatura…</>
                    : <><Sparkles size={14} /> Analisar fatura com IA</>}
                </button>
              )}
            </div>
          )}

          {/* Step 2 — review */}
          {items.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-[#a0b2af]">
                  Reveja as linhas detetadas — pela <span className="text-[#d4af37]">mesma ordem da fatura</span>,
                  de cima para baixo. Edite valores se necessário. Desmarque o que não quer importar.
                </div>
                <button onClick={() => { setItems([]); reset(); }}
                        className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                        data-testid="invoice-restart">
                  Recomeçar
                </button>
              </div>

              {/* Bulk-category actions — quick way to push every selected row
                  straight to «Materiais de limpeza» or bring them back to
                  «Produtos» without clicking row-by-row. */}
              <div className="flex items-center justify-between flex-wrap gap-3 lux-card p-3 border border-[#1a4640]">
                <div className="text-[11px] text-[#a0b2af]">
                  <span className="text-[#d4af37] font-semibold">
                    {items.filter(i => i.selected).length}
                  </span> selecionada(s) · destino atual:
                  <span className="ml-1.5 text-[#fdfbf7]">
                    {items.filter(i => i.selected && i.category !== "material").length} produto(s)
                  </span>
                  <span className="mx-1">·</span>
                  <span className="text-[#fdfbf7]">
                    {items.filter(i => i.selected && i.category === "material").length} material(is)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setItems(rows => rows.map(r => r.selected ? { ...r, category: "produto" } : r))}
                          className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#1a4640] hover:border-[#d4af37] rounded-sm transition-colors"
                          data-testid="invoice-bulk-produto">
                    Marcar como produto
                  </button>
                  <button onClick={() => setItems(rows => rows.map(r => r.selected ? { ...r, category: "material" } : r))}
                          className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 rounded-sm transition-colors"
                          data-testid="invoice-bulk-material">
                    Marcar como material
                  </button>
                </div>
              </div>

              {items.some(i => i.existing) && (
                <div className="border border-[#ef4444]/60 bg-[#ef4444]/10 rounded-sm px-3 py-2 text-xs text-[#fca5a5] flex items-start gap-2"
                     data-testid="invoice-duplicates-hint">
                  <Trash2 size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <strong className="text-[#fdfbf7]">{items.filter(i => i.existing).length} produto(s) já registado(s) neste mês</strong> — marcados a vermelho com etiqueta «Já existe».
                    Já desmarcámos a importação automaticamente. Reveja se quer eliminar a linha antiga ou não duplicar.
                  </div>
                </div>
              )}

              <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-sm" data-testid="invoice-preview-table">
                  <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af] border-b border-[#1a4640]">
                    <tr>
                      <th className="text-left p-2 w-8"></th>
                      <th className="text-center p-2 w-8">#</th>
                      <th className="text-left p-2">Tipo</th>
                      <th className="text-left p-2">Código</th>
                      <th className="text-left p-2">Descrição</th>
                      <th className="text-right p-2">Preço Uni. €</th>
                      <th className="text-right p-2">Qt. Total</th>
                      <th className="text-right p-2">IVA %</th>
                      <th className="text-right p-2">Total €</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const total = (Number(it.unit_price) || 0) * (Number(it.quantity) || 0)
                                    * (1 + (Number(it.vat_rate) || 0) / 100);
                      const isMat = it.category === "material";
                      return (
                        <tr key={it.id}
                            className={`border-b border-[#1a4640]/40 ${it.selected ? "" : "opacity-40"} ${it.existing ? "bg-[#ef4444]/10" : isMat ? "bg-[#d4af37]/5" : ""}`}
                            data-testid={`invoice-row-${it.id}`}>
                          <td className="p-2">
                            <input type="checkbox" checked={it.selected}
                                   onChange={e => patch(it.id, { selected: e.target.checked })}
                                   className="accent-[#d4af37]" />
                          </td>
                          <td className="p-2 text-center text-xs font-display text-[#a0b2af] tabular-nums"
                              data-testid={`invoice-row-num-${it.id}`}>
                            {idx + 1}
                          </td>
                          <td className="p-2">
                            <select value={it.category || "produto"}
                                    onChange={e => patch(it.id, { category: e.target.value })}
                                    className={`border rounded-sm px-1.5 py-1 text-xs outline-none ${isMat ? "bg-[#d4af37]/10 border-[#d4af37] text-[#d4af37] font-semibold" : "bg-[#031c18] border-[#1a4640] focus:border-[#d4af37]"}`}
                                    data-testid={`invoice-row-category-${it.id}`}>
                              <option value="produto">Produto</option>
                              <option value="material">Material</option>
                            </select>
                          </td>
                          <td className="p-2">
                            <input value={it.code}
                                   onChange={e => patch(it.id, { code: e.target.value })}
                                   placeholder="—"
                                   className="w-24 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-xs font-mono text-[#d4af37] outline-none"
                                   data-testid={`invoice-row-code-${it.id}`} />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <input value={it.name}
                                     onChange={e => patch(it.id, { name: e.target.value })}
                                     className={`w-full min-w-[220px] bg-transparent border rounded-sm px-2 py-1 text-sm outline-none ${it.existing ? "border-[#ef4444]/60 focus:border-[#ef4444] text-[#fca5a5]" : "border-[#1a4640] focus:border-[#d4af37]"}`} />
                              {it.existing && (
                                <span className="text-[9px] uppercase tracking-widest text-[#fdfbf7] bg-[#ef4444] px-2 py-0.5 rounded-sm shrink-0 font-semibold animate-pulse"
                                      data-testid={`invoice-duplicate-${it.id}`}
                                      title="Este produto já está registado neste mês — desmarque-o se não quiser duplicar.">
                                  Já existe
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-right">
                            <input type="number" step="0.01" min="0"
                                   value={it.unit_price}
                                   onChange={e => patch(it.id, { unit_price: e.target.value })}
                                   className="w-20 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-1.5 py-1 text-sm text-right outline-none" />
                          </td>
                          <td className="p-2 text-right">
                            <input type="number" step="0.01" min="0"
                                   value={it.quantity}
                                   onChange={e => patch(it.id, { quantity: e.target.value })}
                                   className="w-16 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-1.5 py-1 text-sm text-right outline-none" />
                          </td>
                          <td className="p-2 text-right">
                            <select value={it.vat_rate}
                                    onChange={e => patch(it.id, { vat_rate: Number(e.target.value) })}
                                    className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-1.5 py-1 text-sm outline-none">
                              {PT_VAT_RATES.map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </td>
                          <td className="p-2 text-right text-[#d4af37] tabular-nums font-display">
                            {total.toFixed(2)} €
                          </td>
                          <td className="p-2">
                            <button onClick={() => patch(it.id, { selected: false })}
                                    className="text-[#a0b2af] hover:text-[#ef4444]"
                                    title="Descartar linha">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <footer className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-[#1a4640]">
                <div className="text-sm">
                  <span className="text-[#a0b2af]">{totalCount} linha(s) selecionada(s)</span>
                  {(() => {
                    const selProd = items.filter(i => i.selected && i.category !== "material").length;
                    const selMat = items.filter(i => i.selected && i.category === "material").length;
                    if (totalCount === 0) return null;
                    return (
                      <span className="text-[11px] text-[#a0b2af] ml-2"
                            data-testid="invoice-footer-destination">
                        → <span className="text-[#fdfbf7]">{selProd}</span> em Produtos
                        {selMat > 0 && (
                          <>
                            , <span className="text-[#d4af37]">{selMat}</span> em Materiais
                          </>
                        )}
                      </span>
                    );
                  })()}
                  <span className="text-[#a0b2af] mx-2">·</span>
                  <span className="font-display text-[#d4af37] text-lg">{totalValue.toFixed(2)} €</span>
                </div>
                <button onClick={commit} disabled={saving || totalCount === 0}
                        className="btn-gold px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-50"
                        data-testid="invoice-import-confirm">
                  {saving
                    ? <><Loader2 size={14} className="animate-spin" /> A gravar…</>
                    : <><Check size={14} /> Adicionar {totalCount} linha(s) ao mês</>}
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
